import React, { useState } from "react";
import { Shield, Eye, EyeOff, Check, X } from "lucide-react";
import { api } from "@/lib/api";
const RULES = [
  { id: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { id: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "A number", test: (p) => /\d/.test(p) },
  { id: "symbol", label: "A symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const formatDetail = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
};

export const AdminChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const rulesPassed = RULES.map((r) => ({ ...r, ok: r.test(next) }));
  const allOk = rulesPassed.every((r) => r.ok) && next === confirm && current.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await api.post("/admin/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      if (data.token) localStorage.setItem("recoverymatch_admin_token", data.token);
      setMessage({ ok: true, text: data.message || "Password changed." });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setMessage({ ok: false, text: formatDetail(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm max-w-xl" data-testid="admin-change-password-card">
      <h3 className="font-semibold text-[#1C3A7A] flex items-center gap-2 mb-1">
        <Shield className="w-5 h-5 text-blue-500" /> Change Password
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Changing your password signs out every other admin session immediately.
      </p>
      <form onSubmit={submit} className="space-y-3">
        {[
          { label: "Current password", value: current, set: setCurrent, tid: "admin-current-password-input" },
          { label: "New password", value: next, set: setNext, tid: "admin-new-password-input" },
          { label: "Confirm new password", value: confirm, set: setConfirm, tid: "admin-confirm-password-input" },
        ].map((f) => (
          <div key={f.tid}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                data-testid={f.tid}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1C3A7A]"
                autoComplete="off"
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setShow(!show)}
          data-testid="admin-password-visibility-toggle"
          className="text-xs text-gray-500 flex items-center gap-1"
        >
          {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {show ? "Hide" : "Show"} passwords
        </button>

        <ul className="text-xs space-y-1" data-testid="admin-password-rules">
          {rulesPassed.map((r) => (
            <li key={r.id} className={`flex items-center gap-1 ${r.ok ? "text-green-600" : "text-gray-400"}`}>
              {r.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {r.label}
            </li>
          ))}
          <li className={`flex items-center gap-1 ${next && next === confirm ? "text-green-600" : "text-gray-400"}`}>
            {next && next === confirm ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} Passwords match
          </li>
        </ul>

        {message && (
          <div
            data-testid="admin-change-password-message"
            className={`text-sm rounded-lg px-3 py-2 ${message.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
          >
            {message.text}
          </div>
        )}

        <button
          type="submit"
          disabled={!allOk || busy}
          data-testid="admin-change-password-submit"
          className="bg-[#1C3A7A] text-white rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-40"
        >
          {busy ? "Changing..." : "Change Password"}
        </button>
      </form>
    </div>
  );
};

export default AdminChangePassword;
